function asCount(value) {
  const count = Number(value);
  return Number.isFinite(count) && count > 0 ? count : 0;
}

const ROLE_LABELS = {
  applicant: 'Applicant',
  worker: 'Worker',
  admin: 'Staff',
};

function buildAction({
  key,
  owner,
  title,
  description,
  badge,
  tone,
  staffAction,
  applicantSees,
  applicantDescription = null,
  workerDescription = null,
  primaryLabel = null,
  route = null,
  formId = null,
}) {
  return {
    key,
    owner,
    title,
    description,
    badge,
    tone,
    staffAction,
    applicantSees,
    primaryLabel,
    route,
    formId,
    audience: {
      admin: {
        label: 'Staff next',
        title,
        description: staffAction,
        badge,
        tone,
      },
      applicant: {
        label: 'Applicant sees',
        title: applicantSees,
        description: applicantDescription || (owner === 'applicant' ? description : 'We will update this as soon as the review moves forward.'),
        badge: owner === 'applicant' ? 'Action required' : owner === 'complete' ? 'Complete' : 'Waiting',
        tone: owner === 'applicant' ? tone : owner === 'complete' ? 'success' : 'info',
        primaryLabel,
        route,
        formId,
      },
      worker: {
        label: 'Worker sees',
        title: applicantSees,
        description: workerDescription || (owner === 'applicant' ? description : 'No action is needed from you right now.'),
        badge: owner === 'applicant' ? 'Action required' : owner === 'complete' ? 'Complete' : 'Waiting',
        tone: owner === 'applicant' ? tone : owner === 'complete' ? 'success' : 'info',
        primaryLabel,
        route,
        formId,
      },
    },
  };
}

export function presentApplicantNextAction(action, role = 'applicant') {
  if (!action) return null;
  const audience = action.audience?.[role] || action.audience?.applicant;
  return {
    role,
    roleLabel: ROLE_LABELS[role] || ROLE_LABELS.applicant,
    key: action.key,
    owner: action.owner,
    title: audience?.title || action.title,
    description: audience?.description || action.description,
    badge: audience?.badge || action.badge,
    tone: audience?.tone || action.tone,
    label: audience?.label || ROLE_LABELS[role] || ROLE_LABELS.applicant,
    primaryLabel: audience?.primaryLabel || action.primaryLabel || null,
    route: audience?.route || action.route || null,
    formId: audience?.formId || action.formId || null,
  };
}

export function deriveApplicantNextAction({
  applicationOnFile = true,
  cvFileExists = false,
  cvApproved = false,
  cvReplacementPending = false,
  cvLinkedForReview = false,
  cvReviewReady = false,
  cvLinkRecoveryAvailable = false,
  declarationsOnFile = null,
  declarationsAdequatelyReviewed = false,
  employmentHistoryExists = null,
  employmentReadyForSignOff = false,
  employmentSignedOff = false,
  employmentIntegrity = null,
  employmentGapEvaluation = null,
  employmentStatusBlockers = [],
} = {}) {
  // CS-2: the canonical Application Form requirement (decided on the backend via
  // services/application_source and passed in as applicationOnFile) OUTRANKS every
  // other recruitment task. This is presentation of the backend decision only —
  // application-readiness is NOT recomputed in JavaScript.
  if (!applicationOnFile) {
    return buildAction({
      key: 'application_missing',
      owner: 'applicant',
      title: 'Complete your application',
      description: 'We need your application before we can continue with recruitment.',
      badge: 'Applicant action',
      tone: 'danger',
      staffAction: 'The applicant needs to complete their application. Resend their access link if needed.',
      applicantSees: 'Complete your application',
      applicantDescription: 'We need your application before we can continue with recruitment.',
      workerDescription: 'We need your application before we can continue with recruitment.',
      primaryLabel: 'Continue application',
      route: '/worker/forms/application_form',
      formId: 'application_form',
    });
  }

  if (employmentSignedOff) {
    return buildAction({
      key: 'employment_review_signed_off',
      owner: 'complete',
      title: 'Recruitment review is signed off',
      description: 'The applicant has no action in this section.',
      badge: 'Complete',
      tone: 'success',
      staffAction: 'Continue the recruitment workflow.',
      applicantSees: 'No employment-history action.',
    });
  }

  // Recruitment Hub phase 1: show work the applicant can do NOW before
  // review/processing status. This only changes next-action presentation:
  // the server's CV, gap, integrity and sign-off checks remain authoritative.
  //
  // Admin-owned CV review is never an excuse to hide an incomplete work
  // history, an unexplained gap or an outstanding applicant declaration.
  // Known gaps can be explained while extraction/integrity is processing.
  const integrityState = String(employmentIntegrity?.state || '').toLowerCase();
  const pendingGaps = asCount(employmentGapEvaluation?.pending_count);
  const rejectedGaps = asCount(employmentGapEvaluation?.rejected_count);
  const needsInfoGaps = asCount(employmentGapEvaluation?.needs_info_count);
  const explainedGaps = asCount(employmentGapEvaluation?.explained_count);

  if (cvReplacementPending) {
    return buildAction({
      key: 'cv_replacement_required',
      owner: 'applicant',
      title: 'Applicant needs to upload a replacement CV',
      description: 'Their portal should show a CV replacement task from the admin request.',
      badge: 'Applicant action',
      tone: 'danger',
      staffAction: 'Wait for the replacement CV, or resend their applicant access link.',
      applicantSees: 'Upload replacement CV.',
      primaryLabel: 'Upload replacement CV',
      route: '#documents-cv',
    });
  }

  if (!cvFileExists) {
    return buildAction({
      key: 'cv_missing',
      owner: 'applicant',
      title: 'Applicant needs to upload their CV',
      description: 'No usable CV evidence is currently available for this review.',
      badge: 'Applicant action',
      tone: 'danger',
      staffAction: 'Ask them to use Access my application and upload a CV.',
      applicantSees: 'Upload your CV.',
      primaryLabel: 'Upload CV',
      route: '#documents-cv',
    });
  }

  if (employmentHistoryExists === false) {
    return buildAction({
      key: 'employment_history_missing',
      owner: 'applicant',
      title: 'Applicant needs to add work history',
      description: 'Please check and complete your full employment history before this section can be signed off.',
      badge: 'Applicant action',
      tone: 'warning',
      staffAction: 'Ask them to add or check their work history.',
      applicantSees: 'Add work history.',
      primaryLabel: 'Add work history',
      route: '#employment-history',
    });
  }

  if (pendingGaps || rejectedGaps || needsInfoGaps) {
    const total = pendingGaps + rejectedGaps + needsInfoGaps;
    return buildAction({
      key: 'employment_gap_action_required',
      owner: 'applicant',
      title: total === 1 ? 'Applicant needs to update an employment gap' : 'Applicant needs to update employment gaps',
      description: 'Their portal should take them back to the employment-gap section, not a fresh CV loop.',
      badge: 'Applicant action',
      tone: rejectedGaps || needsInfoGaps ? 'danger' : 'warning',
      staffAction: 'Use Reject or Request info on the gap, then ask them to open Access my application.',
      applicantSees: 'Explain employment gaps.',
      primaryLabel: 'Explain employment gaps',
      route: '#employment-gaps-section',
    });
  }

  if (declarationsOnFile === false) {
    return buildAction({
      key: 'declarations_missing',
      owner: 'applicant',
      title: 'Complete your declarations',
      description: 'We need a few final confirmations before your application can continue.',
      badge: 'Applicant action',
      tone: 'warning',
      staffAction: 'Ask the applicant to open their application and complete the declarations step.',
      applicantSees: 'Complete your declarations.',
      primaryLabel: 'Continue',
      route: '/worker/forms/applicant_declarations',
      formId: 'applicant_declarations',
    });
  }

  if (integrityState === 'action_required') {
    return buildAction({
      key: 'employment_integrity_action_required',
      owner: 'applicant',
      title: 'Applicant needs to check their work history',
      description: employmentIntegrity?.reason || 'CareOps found an employment-history item that needs the applicant’s response.',
      badge: 'Applicant action',
      tone: 'warning',
      staffAction: 'Ask the applicant to open the Employment History Check and resolve the highlighted gaps or material CV/history differences.',
      applicantSees: 'Check your work history.',
      applicantDescription: employmentIntegrity?.reason || 'Resolve the highlighted employment-history items before this section can be completed.',
      workerDescription: employmentIntegrity?.reason || 'Resolve the highlighted employment-history items before this section can be completed.',
      primaryLabel: 'Check work history',
      route: '#employment-integrity-section',
    });
  }

  if (integrityState === 'confirmation_required') {
    return buildAction({
      key: 'employment_integrity_confirmation_required',
      owner: 'applicant',
      title: 'Applicant needs to confirm their employment history',
      description: 'The automated pre-check is complete; the applicant must confirm the current official history is complete and accurate.',
      badge: 'Applicant action',
      tone: 'warning',
      staffAction: 'Ask the applicant to open the Employment History Check and confirm the current official history.',
      applicantSees: 'Confirm your employment history.',
      applicantDescription: 'CareOps has finished checking the current information. Review it and confirm your official employment history is complete and accurate.',
      workerDescription: 'CareOps has finished checking the current information. Review it and confirm your official employment history is complete and accurate.',
      primaryLabel: 'Review and confirm',
      route: '#employment-integrity-section',
    });
  }

  if (integrityState === 'checking') {
    return buildAction({
      key: 'employment_integrity_checking',
      owner: 'system',
      title: 'CareOps is checking employment history',
      description: 'The latest CV and official employment history are being compared.',
      badge: 'System check',
      tone: 'info',
      staffAction: 'No manual action yet — CareOps is checking the current applicant data.',
      applicantSees: 'Checking your employment history…',
      applicantDescription: 'Please wait while CareOps checks your latest CV and official work history. You can continue other independent onboarding tasks.',
      workerDescription: 'Please wait while CareOps checks your latest CV and official work history. You can continue other independent onboarding tasks.',
      route: '#employment-integrity-section',
    });
  }

  if (integrityState === 'error') {
    return buildAction({
      key: 'employment_integrity_error',
      owner: 'system',
      title: 'Employment-history check needs retrying',
      description: employmentIntegrity?.reason || 'CareOps could not finish the latest employment-history check.',
      badge: 'System check',
      tone: 'warning',
      staffAction: 'No evidence has been lost. The applicant can retry the employment-history check from their dashboard.',
      applicantSees: 'Employment-history check needs retrying.',
      applicantDescription: employmentIntegrity?.reason || 'Your information is saved. Retry the check before this employment-history section can be completed.',
      workerDescription: employmentIntegrity?.reason || 'Your information is saved. Retry the check before this employment-history section can be completed.',
      primaryLabel: 'Retry check',
      route: '#employment-integrity-section',
    });
  }

  if (explainedGaps) {
    return buildAction({
      key: 'employment_gap_admin_review',
      owner: 'admin',
      title: explainedGaps === 1 ? 'Admin needs to verify the gap explanation' : 'Admin needs to verify gap explanations',
      description: 'The applicant has done their part; verify or reject the explanation before sign-off.',
      badge: 'Admin action',
      tone: 'warning',
      staffAction: 'Review the explanation in Employment Gap Verification.',
      applicantSees: 'Waiting for review.',
    });
  }

  if (!cvLinkedForReview && (cvLinkRecoveryAvailable || cvFileExists)) {
    return buildAction({
      key: 'cv_link_for_review',
      owner: 'admin',
      title: 'Admin needs to link the CV for review',
      description: 'The CV exists, but Review CV is not enabled against the active file yet.',
      badge: 'Admin action',
      tone: 'info',
      staffAction: 'Click Link CV for review, then run Review CV.',
      applicantSees: 'Waiting for review.',
    });
  }

  if (!cvApproved) {
    return buildAction({
      key: cvReviewReady ? 'cv_admin_review' : 'cv_prepare_review',
      owner: 'admin',
      title: cvReviewReady ? 'Admin needs to review and approve the CV' : 'Admin needs to prepare CV review',
      description: cvReviewReady
        ? 'The CV is ready for review. Approve it only after the employment-history check is acceptable.'
        : 'The CV is on file but not ready for review yet.',
      badge: 'Admin action',
      tone: 'info',
      staffAction: cvReviewReady ? 'Open Review CV and approve or request a replacement.' : 'Link or recover the CV before review.',
      applicantSees: 'Waiting for review.',
    });
  }

  if (!declarationsAdequatelyReviewed) {
    return buildAction({
      key: 'declarations_admin_review',
      owner: 'admin',
      title: 'Declarations received',
      description: "Review the applicant's declarations before continuing.",
      badge: 'Admin action',
      tone: 'info',
      staffAction: 'Open Review declarations.',
      applicantSees: 'Declarations received.',
      applicantDescription: 'We have your declarations. The recruitment team will review them.',
      workerDescription: 'We have your declarations. The recruitment team will review them.',
    });
  }

  if (employmentReadyForSignOff) {
    return buildAction({
      key: 'employment_review_ready_for_signoff',
      owner: 'admin',
      title: 'Ready for admin sign-off',
      description: 'The applicant should not need to do anything else in this section.',
      badge: 'Ready',
      tone: 'success',
      staffAction: 'Sign off employment review.',
      applicantSees: 'Waiting for review.',
    });
  }

  const blockerSummary = (employmentStatusBlockers || []).filter(Boolean).slice(0, 2).join(' - ');
  return buildAction({
    key: 'employment_review_blocked',
    owner: 'admin',
    title: 'Admin needs to clear review blockers',
    description: blockerSummary || 'This section still has outstanding review checks.',
    badge: 'Admin action',
    tone: 'warning',
    staffAction: 'Work through the blockers shown on this tab.',
    applicantSees: 'Waiting for review unless a new request is sent.',
  });
}
